import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Div } from 'styles';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export function DashChart({ list, ...props }) {
  const chartHeight = props.$height;

  const chartType = list.data1?.name?.slice(-2); // '온도', '습도', '풍향', '풍속' 중 하나

  const dataItems = [];
  if (list.data1) dataItems.push(list.data1);
  if (list.data2) dataItems.push(list.data2);

  const datasets = dataItems
    .map((dataItem, i) => {
      const tempData = [dataItem.data[0], ...dataItem.data, dataItem.data[dataItem.data.length - 1]];

      return {
        label: dataItem.name,
        data: tempData,
        fill: true,
        backgroundColor: (context) => {
          const gradient = context.chart.ctx.createLinearGradient(0, 0, 0, chartHeight);

          if (i === 1) {
            gradient.addColorStop(0, '#FFD3C6');
            gradient.addColorStop(0.335, 'rgba(255, 211, 198, 0.67)');
            gradient.addColorStop(1, 'rgba(255, 211, 198, 0)');
          } else if (i === 0) {
            gradient.addColorStop(0, '#AEE3E5');
            gradient.addColorStop(0.335, 'rgba(223, 244, 245, 0.5)');
            gradient.addColorStop(1, 'rgba(223, 244, 245, 0)');
          }

          return gradient;
        },
        borderColor: i === 1 ? '#FFD3C6' : '#AEE3E5',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2,
      };
    })
    .reverse();

  const data = {
    labels: ['', ...list.labels, ''],
    datasets: datasets,
  };

  /**
   * Helper function to get y-scale configuration based on chart type
   * @param {string} chartType - '온도', '습도', '풍향', '풍속'
   * @returns {object} Configuration object with `max`, `min`, and `stepSize`
   */
  const getYScaleConfig = (chartType) => {
    switch (chartType) {
      case '온도':
        return { max: 50, min: -20, stepSize: 5 }; // 온도: 최대 50, 최소 30, 스텝 10
      case '습도':
        return { max: 100, min: 0, stepSize: 10 }; // 습도: 최대 100, 최소 0, 스텝 10
      case '풍향':
        return { max: 400, min: 0, stepSize: 90 }; // 풍향: 최대 400, 최소 0, 스텝 90
      case '풍속':
        return { max: 50, min: 0, stepSize: 10 }; // 풍속: 최대 5, 최소 0, 스텝 1
      default:
        return { max: 100, min: 0, stepSize: 10 }; // 기본값
    }
  };

  // Get the y-axis scale configuration based on the chart type
  const yScaleConfig = getYScaleConfig(chartType);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        align: 'end',
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 6,
          boxHeight: 6,
          color: 'rgba(23, 23, 23, 0.87)',
          font: {
            family: 'Pretendard',
            size: 14,
            weight: '500',
            lineHeight: 1.5,
          },
          generateLabels: (chart) => {
            const { datasets } = chart.data;
            return datasets
              .slice()
              .reverse()
              .map((dataset, i) => ({
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                pointStyle: 'circle',
                hidden: chart.isDatasetVisible(datasets.length - 1 - i) === false,
                index: datasets.length - 1 - i,
              }));
          },
        },
        onClick: (e, legendItem, legend) => {
          const ci = legend.chart;
          const datasetIndex = legendItem.index;
          const meta = ci.getDatasetMeta(datasetIndex);

          meta.hidden = !meta.hidden;

          ci.update();
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: yScaleConfig.max, // Use max from yScaleConfig
        min: yScaleConfig.min, // Use min from yScaleConfig
        ticks: {
          padding: 10,
          stepSize: yScaleConfig.stepSize, // Use stepSize from yScaleConfig
          font: {
            family: 'Pretendard',
            size: 12,
            weight: '500',
            lineHeight: 1.5,
          },
          color: 'rgb(23, 23, 23, 0.7)',
          callback: function (value) {
            return value.toString();
          },
        },
        grid: {
          drawTicks: false,
        },
      },
      x: {
        display: true,
        grid: {
          drawBorder: false,
          borderWidth: 0,
          color: 'transparent',
        },
        ticks: {
          align: 'center',
          font: {
            family: 'Pretendard',
            size: 12,
            weight: '500',
            lineHeight: 1.5,
          },
          color: 'rgb(23, 23, 23, 0.7)',
        },
      },
    },
  };

  return (
    <Div $width="100%" {...props}>
      <Line data={data} options={options} />
    </Div>
  );
}
